// Multi-select dropdown list with checkboxes

import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import {
  useFloating,
  offset,
  flip,
  shift,
  size,
  FloatingPortal,
} from '@floating-ui/react';
import GetClickOutside from './GetClickOutside';
import CheckBox from './CheckBox';
import ChevronUpDown from './ChevronUpDown';
import FilterInput from './FilterInput';
import './MultiSelect.scss';

export default function MultiSelect({
  options,
  value,
  onChange,
  small,
  className,
  placeholder,
  error,
  search,
  sortMode,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [optionsLocal, setOptionsLocal] = useState(options);
  const listRef = useRef(null);
  const ignoreNextClickRef = useRef(false);

  useEffect(() => {
    setOptionsLocal(options);
  }, [options]);

  // Keep selection ordered exactly as in local options
  const normalizeSelection = (arr, opts) => {
    const seen = new Set();
    const uniq = [];
    for (const v of arr) {
      if (!seen.has(v)) {
        seen.add(v);
        uniq.push(v);
      }
    }

    const optsValues = opts.map((o) => o.value);
    const uniqSet = new Set(uniq);
    const optionsSet = new Set(optsValues);

    const ordered = optsValues.filter((v) => uniqSet.has(v));
    const extras = uniq.filter((v) => !optionsSet.has(v));

    return [...ordered, ...extras];
  };

  const onChangeNorm = useCallback(
    (arr, opts = optionsLocal) => onChange(normalizeSelection(arr, opts)),
    [onChange, optionsLocal],
  );

  const moveOption = useCallback(
    (optionValue, dir) => {
      setOptionsLocal((prev) => {
        const idx = prev.findIndex((o) => o.value === optionValue);
        if (idx === -1) return prev;
        const nextIdx = idx + dir;
        if (nextIdx < 0 || nextIdx >= prev.length) return prev;
        const next = [...prev];
        [next[idx], next[nextIdx]] = [next[nextIdx], next[idx]];
        onChangeNorm(value, next);
        return next;
      });
    },
    [value, onChangeNorm],
  );

  // Compute filtered options (case-insensitive on label)
  const filteredOptions = useMemo(() => {
    if (!search || !query) return optionsLocal;
    const q = query.toLowerCase();
    return optionsLocal.filter((o) =>
      String(o.label).toLowerCase().includes(q),
    );
  }, [optionsLocal, query, search]);

  // Labels for the trigger chip
  const selectedLabels = useMemo(() => {
    const labelByValue = new Map(optionsLocal.map((o) => [o.value, o.label]));
    return value.map((v) => labelByValue.get(v)).filter((l) => l !== undefined);
  }, [optionsLocal, value]);

  useEffect(() => {
    if (!isOpen) setQuery('');
  }, [isOpen]);

  const { refs, floatingStyles, update } = useFloating({
    placement: 'bottom-start',
    strategy: 'fixed',
    middleware: [
      offset(8),
      flip({ fallbackPlacements: ['top-start', 'top-end'] }),
      shift({ padding: 8 }),
      size({
        padding: 8,
        apply({ rects, elements, availableHeight }) {
          Object.assign(elements.floating.style, {
            minWidth: `${Math.round(rects.reference.width)}px`,
            maxWidth: 'min(92vw, 720px)',
          });
          const searchRow = elements.floating.querySelector('.search-row');
          const headerH = searchRow ? searchRow.offsetHeight : 0;
          const okBtn = elements.floating.querySelector('.ok-btn');
          const okBtnH = okBtn ? okBtn.offsetHeight : 0;
          const cs = getComputedStyle(elements.floating);
          const paddingY =
            parseFloat(cs.paddingTop) + parseFloat(cs.paddingBottom);
          const maxListH = Math.max(
            availableHeight - headerH - okBtnH - paddingY - 2,
            96,
          );
          if (listRef.current) {
            Object.assign(listRef.current.style, {
              maxHeight: `${Math.floor(maxListH)}px`,
              overflowY: 'auto',
            });
          }
        },
      }),
    ],
  });

  useEffect(() => {
    if (isOpen) update?.();
  }, [isOpen, update, query, filteredOptions.length]);

  return (
    <div
      className={
        'multi-select' +
        (small ? ' small' : '') +
        (className ? ' ' + className : '')
      }
    >
      <div
        ref={refs.setReference}
        className={'selected-options' + (error ? ' error' : '')}
        onClick={() => {
          if (ignoreNextClickRef.current) return;
          setIsOpen((v) => !v);
        }}
        role='button'
        aria-haspopup='dialog'
        aria-expanded={isOpen}
        aria-label={
          placeholder && value.length > 0
            ? `${placeholder}: ${selectedLabels.join(', ')}`
            : placeholder
        }
      >
        <p className={'value' + (value.length > 0 ? '' : ' placeholder')}>
          {value.length > 0 ? selectedLabels.join(', ') : placeholder}
        </p>
        <ChevronUpDown up={isOpen} />
        <p className={'error-msg' + (error ? ' visible' : '')}>{error}</p>
        <p className={'label' + (value.length > 0 ? ' visible' : '')}>
          {placeholder}
        </p>
      </div>

      <FloatingPortal>
        <GetClickOutside
          active={isOpen}
          onClick={() => {
            setIsOpen(false);
            ignoreNextClickRef.current = true;
            setTimeout(() => {
              ignoreNextClickRef.current = false;
            }, 300);
          }}
        >
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            className={
              'multi-select-floating' +
              (isOpen ? ' open' : '') +
              (small ? ' small' : '')
            }
            role='dialog'
            aria-label={placeholder || 'Multi select'}
          >
            <div className='options' ref={listRef}>
              {search && (
                <div
                  className='search-row'
                  onClick={(e) => e.stopPropagation()}
                >
                  <FilterInput value={query} onChange={setQuery} small />
                </div>
              )}

              {(() => {
                const target = search && query ? filteredOptions : optionsLocal;
                const targetValues = target.map((o) => o.value);
                const allChecked =
                  target.length > 0 &&
                  targetValues.every((v) => value.includes(v));
                const noResults =
                  search && query.trim() && filteredOptions.length === 0;

                return (
                  <>
                    {noResults ? (
                      <p className='no-results'>
                        No results found for your search
                      </p>
                    ) : (
                      <CheckBox
                        key='all'
                        label='Select all'
                        small={small}
                        value={allChecked}
                        onChange={(checked) => {
                          if (checked) {
                            onChangeNorm([
                              ...new Set([...value, ...targetValues]),
                            ]);
                          } else {
                            const tset = new Set(targetValues);
                            onChangeNorm(value.filter((v) => !tset.has(v)));
                          }
                        }}
                        className='select-all'
                      />
                    )}

                    {filteredOptions.map((option) => {
                      const localIndex = optionsLocal.findIndex(
                        (o) => o.value === option.value,
                      );
                      const canUp = localIndex > 0;
                      const canDown =
                        localIndex !== -1 &&
                        localIndex < optionsLocal.length - 1;

                      return (
                        <div
                          key={String(option.value)}
                          className={
                            'multi-select-item' + (sortMode ? ' sort-mode' : '')
                          }
                        >
                          <CheckBox
                            label={option.label}
                            small={small}
                            value={value.includes(option.value)}
                            onChange={(checked) => {
                              if (checked) {
                                onChangeNorm([
                                  ...new Set([...value, option.value]),
                                ]);
                              } else {
                                onChangeNorm(
                                  value.filter((v) => v !== option.value),
                                );
                              }
                            }}
                          />

                          {sortMode && (
                            <div className='btns-wrap'>
                              <button
                                className='up-btn small transparent fa-up-arrow'
                                disabled={!canUp}
                                aria-label={`Move up ${option.label}`}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  moveOption(option.value, -1);
                                }}
                              />
                              <button
                                className='down-btn small transparent fa-down-arrow'
                                disabled={!canDown}
                                aria-label={`Move down ${option.label}`}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  moveOption(option.value, 1);
                                }}
                              />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </>
                );
              })()}
            </div>
            <button className={'ok-btn small'} onClick={() => setIsOpen(false)}>
              Ok
            </button>
          </div>
        </GetClickOutside>
      </FloatingPortal>
    </div>
  );
}
